import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Home from "./pages/Home";
import BalanceSheet from "./pages/BalanceSheet";
import AssetsPage from "./AssetPage";
import PhysicalAssetsPage from "./PhysicalAssetsPage";
import ResiPropertyList from "./ResiPropertyList";
import ResiPropertyDetail from "./ResiPropertyDetail";
import LandList from "./LandList";
import LandDetail from "./LandDetail";
import StructureList from "./StructureList";
import StructureDetail from "./StructureDetail";
import DebtPage from "./DebtPage";
import MortgageList from "./MortgageList";
import MortgageDetail from "./MortgageDetail";
import AutoLoanList from "./AutoLoanList";
import AutoLoanDetail from "./AutoLoanDetail";
import RevolversPage from "./RevolversPage";
import CreditCardDetail from "./CreditCardDetail";
import RevolversHelocPage from "./RevolverHelocsPage";
import HelocDetail from "./HelocDetail";
import CreateLoan from "./CreateLoan";
import CreatePhysical from "./CreatePhysical";
import CreateResiProperty from "./CreateResiProperty";
import VehicleDetail from "./VehicleDetail";
import BankAccounts from "./components/BankAccounts";
import IncomeStatement from "./pages/IncomeStatement";
import CashAssets from "./pages/CashAssets";
import CashAccountDetail from "./CashAccountDetail";
import Equity from "./pages/Equity";
import IncomePage from "./pages/IncomePage";
import ExpensesPage from "./pages/ExpensesPage";
import BudgetsPage from "./pages/BudgetsPage";
import CashFlow from "./pages/CashFlow";
import Login from './components/Login';
import axios from 'axios';

// Add default base URL for axios
axios.defaults.baseURL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
    ? `http://${window.location.hostname}:8000` 
    : '';

const App = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Check if user is already logged in
        const token = localStorage.getItem('accessToken');
        if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            // If you have user info in localStorage, restore it too
            const savedUser = localStorage.getItem('user');
            if (savedUser) {
                setUser(JSON.parse(savedUser));
            } else {
                setUser({ token, username: 'User' });
            }
        }
        setLoading(false);
    }, []);

    const handleLogin = (userData) => {
        if (userData && (userData.access || userData.accessToken)) {
            const accessToken = userData.access || userData.accessToken;
            const refreshToken = userData.refresh || userData.refreshToken;
            localStorage.setItem('accessToken', accessToken);
            localStorage.setItem('refreshToken', refreshToken);
            localStorage.setItem('user', JSON.stringify(userData));
            axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        }
        setUser(userData);
    };

    const handleLogout = () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
        window.location.href = '/';
    };

    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        try {
            const saved = localStorage.getItem('sidebarCollapsed');
            return saved ? JSON.parse(saved) : false;
        } catch (e) { return false; }
    });

    useEffect(() => {
        try {
            localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed));
        } catch (e) { /* ignore */ }
    }, [sidebarCollapsed]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!user) {
        return (
            <Router>
                <Routes>
                    <Route path="*" element={<Login onLogin={handleLogin} />} />
                </Routes>
            </Router>
        );
    }

    return (
        <Router>
            <div className={`app-shell ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
                {sidebarCollapsed && (
                    <button
                        className="sidebar-expand-tab"
                        onClick={() => setSidebarCollapsed(false)}
                        title="Expand sidebar"
                        aria-label="Expand sidebar"
                    >
                        »
                    </button>
                )}
                <Sidebar
                    collapsed={sidebarCollapsed}
                    onToggle={() => setSidebarCollapsed(prev => !prev)}
                    onLogout={handleLogout}
                />
                <main className="main-content">
                    <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/balance_sheet" element={<BalanceSheet />} />
                        <Route path="/balance_sheet/assets" element={<AssetsPage />} />
                        <Route path="/balance_sheet/assets/physical_assets" element={<PhysicalAssetsPage />} />
                        <Route path="/balance_sheet/assets/invested_assets" element={<Navigate to="/balance_sheet/assets/invested_assets/all" replace />} />
                        <Route path="/balance_sheet/assets/invested_assets/:id" element={<Navigate to="/balance_sheet/assets/invested_assets/all" replace />} />
                        <Route path="/balance_sheet/assets/cash_assets" element={<CashAssets />} />
                        <Route path="/balance_sheet/assets/cash_assets/:id" element={<CashAccountDetail />} />
                        <Route path="/balance_sheet/equity" element={<Equity />} />
                        <Route path="/balance_sheet/financing" element={<DebtPage />} />
                        <Route path="/balance_sheet/financing/mortgages" element={<MortgageList />} />
                        <Route path="/balance_sheet/financing/mortgages/:id" element={<MortgageDetail />} />
                        <Route path="/balance_sheet/financing/auto_loans" element={<AutoLoanList />} />
                        <Route path="/balance_sheet/financing/auto_loans/:id" element={<AutoLoanDetail />} />
                        <Route path="/balance_sheet/financing/revolvers" element={<RevolversPage />} />
                        <Route path="/balance_sheet/financing/revolvers/credit-cards/:id" element={<CreditCardDetail />} />
                        <Route path="/balance_sheet/financing/revolvers/helocs" element={<RevolversHelocPage />} />
                        <Route path="/balance_sheet/financing/revolvers/helocs/:id" element={<HelocDetail />} />
                        <Route path="/balance_sheet/financing/create-loan" element={<CreateLoan />} />
                        <Route path="/income_statement" element={<IncomeStatement />} />
                        <Route path="/income_statement/income" element={<IncomePage />} />
                        <Route path="/income_statement/expenses" element={<ExpensesPage />} />
                        <Route path="/income_statement/budgets" element={<BudgetsPage />} />
                        <Route path="/cash_flow" element={<CashFlow />} />
                        <Route path="/balance_sheet/assets/physical_assets/create-physical" element={<CreatePhysical />} />
                        <Route path="/balance_sheet/assets/physical_assets/resi_properties/create-resi-property" element={<CreateResiProperty />} />
                        <Route path="/balance_sheet/assets/physical_assets/resi_properties" element={<ResiPropertyList />} />
                        <Route path="/balance_sheet/assets/physical_assets/resi_properties/:id" element={<ResiPropertyDetail />} />
                        <Route path="/balance_sheet/assets/physical_assets/resi_properties/land" element={<LandList />} />
                        <Route path="/balance_sheet/assets/physical_assets/resi_properties/land/:id" element={<LandDetail />} />
                        <Route path="/balance_sheet/assets/physical_assets/resi_properties/structures" element={<StructureList />} />
                        <Route path="/balance_sheet/assets/physical_assets/resi_properties/structures/:id" element={<StructureDetail />} />
                        <Route path="/balance_sheet/assets/physical_assets/vehicles/:id" element={<VehicleDetail />} />
                        <Route path="/bank-accounts" element={<BankAccounts />} />
                        <Route path="*" element={<Home />} />
                    </Routes>
                </main>
            </div>
        </Router>
    );
};

export default App;
